import { zodResolver } from '@hookform/resolvers/zod';
import {
  NOTIFICATION_EMAIL_NOTICE,
  ROLES,
  ROLE_LABELS,
  TEAM_LEVELS,
  TEAM_LEVEL_LABELS,
  createTeamMemberSchema,
  type CreateTeamMemberInput,
  type TeamLevel,
  type TeamMember,
} from '@easyhelp/shared';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { Controller, useForm } from 'react-hook-form';

/** Ruolo proposto quando si aggiunge una persona a un livello (modificabile). */
const DEFAULT_ROLE_BY_LEVEL: Record<TeamLevel, CreateTeamMemberInput['role']> = {
  primo: 'analista',
  secondo: 'sviluppo',
};

export interface MemberDialogProps {
  open: boolean;
  /** Membro da modificare; assente in aggiunta. */
  member?: TeamMember;
  /** Livello preselezionato in aggiunta. */
  level: TeamLevel;
  /** Errore dell'API da mostrare senza chiudere il dialogo. */
  error?: string | null;
  busy?: boolean;
  onSubmit: (values: CreateTeamMemberInput) => void;
  onClose: () => void;
}

/**
 * Aggiunta o modifica di un membro del team. In modifica l'email Google non si cambia: è la
 * chiave d'accesso alla suite (DEC-007); una persona diversa va aggiunta come nuovo membro.
 */
export function MemberDialog({
  open,
  member,
  level,
  error = null,
  busy = false,
  onSubmit,
  onClose,
}: MemberDialogProps) {
  const editing = Boolean(member);
  const { control, handleSubmit, reset } = useForm<CreateTeamMemberInput>({
    resolver: zodResolver(createTeamMemberSchema),
    defaultValues: {
      name: member?.name ?? '',
      email: member?.email ?? '',
      notificationEmail: member?.notificationEmail ?? '',
      role: member?.role ?? DEFAULT_ROLE_BY_LEVEL[level],
      level: member?.level ?? level,
    },
  });

  const close = () => {
    reset();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : close}
      fullWidth
      maxWidth="sm"
      aria-labelledby="member-dialog-title"
    >
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogTitle id="member-dialog-title">{editing ? `Modifica ${member?.name}` : 'Aggiungi al team'}</DialogTitle>
        <DialogContent>
          <Stack spacing={4} sx={{ pt: 1 }}>
            <Controller
              name="name"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label="Nome"
                  required
                  autoFocus
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                />
              )}
            />
            <Controller
              name="email"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label="Email aziendale Google"
                  type="email"
                  required
                  disabled={editing}
                  error={Boolean(fieldState.error)}
                  helperText={
                    fieldState.error?.message ??
                    (editing
                      ? "È la chiave d'accesso: non si cambia. Una persona diversa va aggiunta come nuovo membro."
                      : "Con questo account la persona entra nella suite: aggiungerla qui è l'unico modo per abilitarne l'accesso.")
                  }
                />
              )}
            />
            <Controller
              name="notificationEmail"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  value={field.value ?? ''}
                  label="Email di notifica (se diversa)"
                  type="email"
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message ?? NOTIFICATION_EMAIL_NOTICE}
                />
              )}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={4}>
              <Controller
                name="level"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField {...field} select label="Livello" fullWidth error={Boolean(fieldState.error)}>
                    {TEAM_LEVELS.map((value) => (
                      <MenuItem key={value} value={value}>
                        {TEAM_LEVEL_LABELS[value]}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
              <Controller
                name="role"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField {...field} select label="Ruolo" fullWidth error={Boolean(fieldState.error)}>
                    {ROLES.map((value) => (
                      <MenuItem key={value} value={value}>
                        {ROLE_LABELS[value]}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Stack>
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={busy}>
            Annulla
          </Button>
          <Button type="submit" variant="contained" disabled={busy}>
            {editing ? 'Salva' : 'Aggiungi'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
